import type { Dict } from './de'

export const en: Dict = {
  app: { name: 'StaySuite' },
  account: {
    title: 'Account',
    customerNumber: 'Customer number',
    imprint: 'Legal notice',
    privacy: 'Privacy',
    terms: 'Terms',
    privacyLong: 'Privacy notice',
    support: 'Please quote the customer number when contacting support.',
  },
  kontoLoeschung: {
    eintrag: 'Delete account',
    titel: 'Delete account',
    intro: 'Your StaySuite account will be deleted: your sign-in, your credentials and your memberships. The deletion is scheduled and carried out after {tage} days — until then you can cancel it at any time by signing in.',
    neuStartLeer: 'A later registration with the same email address starts empty. Your previous data will no longer be accessible.',
    belegeBleiben: 'Issued invoices and their credit notes are subject to statutory retention. They are not deleted but kept dormant until that period expires.',
    betriebZu: 'You are the only owner: your business “{name}” will be closed.',
    mitgliedVerliert: '1 other member will lose access.',
    mitgliederVerlieren: '{n} other members will lose access.',
    aboStripe: 'Your subscription will end at the end of the current billing period.',
    aboApple: 'Your subscription runs through Apple. Cancel it yourself in the iOS Settings under “Subscriptions” — the deletion does not wait for that.',
    aboGoogle: 'Your subscription runs through Google Play. Cancel it yourself in the Play Store under “Subscriptions” — the deletion does not wait for that.',
    passwort: 'Password',
    passwortHint: 'To confirm, enter your account password.',
    bestaetigen: 'Delete account',
    reauthFehler: 'Confirmation failed. Please check your input and try again.',
    vormerkenFehler: 'That did not work just now. Please try again.',
    vorschauFehler: 'The overview could not be loaded. Please try again.',
    erneut: 'Try again',
    fertig: 'Your deletion is scheduled. Your account will be deleted on {datum}.',
    fertigAbmeldung: 'You will now be signed out. If you sign in again before that date, you can still cancel the deletion.',
    abmelden: 'Understood — sign out',
    dialogTitel: 'Your account is set to be deleted',
    dialogText: 'Your account will be deleted on {datum}. Until then you can cancel the deletion and continue working as usual.',
    dialogTextOhneDatum: 'A deletion is scheduled for your account. Until the period expires you can cancel it and continue working as usual.',
    dialogAbbrechen: 'Cancel deletion',
    dialogBeibehalten: 'Keep deletion',
    dialogFehler: 'That did not work just now. Please try again.',
  },
  checkin: {
    title: 'Online check-in',
    arrivingAround: 'arriving around',
    registration: 'Registration form',
    open: 'Not filled in yet',
    submitted: 'Registration received',
  },
  books: {
    title: 'Reports',
  },
  nav: {
    calendar: 'Calendar',
    bookings: 'Bookings',
    guests: 'Guests',
    billing: 'Billing',
    rates: 'Rates',
    settings: 'Settings',
    settingsShort: 'Setup',
    billingShort: 'Billing',
    today: 'Today',
    more: 'More',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    new: 'New',
    back: 'Back',
    search: 'Search',
    none: 'None',
    from: 'from',
    to: 'to',
    nights: 'nights',
    night: 'night',
    adults: 'Adults',
    children: 'Children',
    total: 'Total',
    net: 'Net',
    vat: 'VAT',
    gross: 'Gross',
    open: 'Open',
    paid: 'Paid',
    loading: 'Loading …',
    noResults: 'Nothing found',
    optional: 'optional',
  },
  status: {
    enquiry: 'Enquiry',
    quoted: 'Quoted',
    option: 'Option',
    confirmed: 'Confirmed',
    checked_in: 'Checked in',
    checked_out: 'Checked out',
    cancelled: 'Cancelled',
    no_show: 'No-show',
    block: 'Blocked',
  },
  channel: {
    direct: 'Direct',
    website: 'Website',
    phone: 'Phone',
    email: 'Email',
    booking_com: 'Booking.com',
    airbnb: 'Airbnb',
    vrbo: 'Vrbo',
    other: 'Other',
  },
  calendar: {
    title: 'Availability',
    unit: 'Unit',
    free: 'Free',
    occupancy: 'Occupancy',
    arrivals: 'Arrivals',
    departures: 'Departures',
    conflict: 'Conflicts with portal calendar',
    month: 'Month',
    dragHint: 'Drag across dates to book',
    gripHint: 'Drag a bar’s ends to change arrival and departure',
    rowHeightHint: 'Drag to change the row height · double-click to reset',
    newBooking: 'New booking',
    blockDates: 'Block dates',
    bar: {
      adult: '1 adult',
      adults: '{n} adults',
      child: '1 child',
      children: '{n} children',
      prepayNone: 'No prepayment requested',
      prepayOpen: 'Prepayment outstanding',
      prepayReceived: 'Prepayment received',
      invoiceNone: 'No invoice yet',
      invoiceMissing: 'Stay is over, still no invoice',
      invoiceDraft: 'Invoice still a draft',
      invoiceOpen: 'Invoice issued, still unpaid',
      invoiceOverdue: 'Invoice overdue',
      invoicePaid: 'Invoice paid',
      invoiceCancelled: 'Invoice cancelled',
      pet: 'Pet booked',
    },
  },
  booking: {
    title: 'Booking',
    reference: 'Reference',
    channel: 'Channel',
    extras: 'Extras',
    guest: 'Guest',
    unit: 'Unit',
    arrival: 'Arrival',
    departure: 'Departure',
    occupancy: 'Occupancy',
    price: 'Price',
    breakdown: 'Breakdown',
    prepayment: 'Prepayment',
    ofTotal: 'of the total',
    deposit: 'Security deposit',
    notes: 'Note for the guest',
    address: 'Street and number',
    postcode: 'Postcode',
    city: 'Town',
    docLanguage: 'Document language',
    internalNotes: 'Internal note',
    createQuote: 'Create quote',
    createInvoice: 'Create invoice',
    registration: 'Registration form',
    perNight: 'per night',
    minNights: 'Minimum stay {n} nights',
    tooManyGuests: 'More guests than the unit allows ({max})',
    overlaps: 'These dates are already taken',
    departureBeforeArrival: 'Departure must be after arrival',
    softOverlap: 'Careful: {who} already has an enquiry for these dates — the unit is not firmly booked yet.',
    optionUntil: 'Hold valid until',
    optionUntilHint: 'The unit is released automatically after this date',
    optionExpiring: 'Holds expiring soon',
    optionExpires: 'Hold expires today',
    optionExpiresIn: 'Hold expires in {n} days',
  },
  deposit: {
    title: 'Security deposit',
    not_required: 'Not required',
    due: 'Due',
    held: 'Held',
    partially_retained: 'Partly retained',
    retained: 'Retained',
    released: 'Refunded',
    release: 'Refund deposit',
    retain: 'Retain from deposit',
  },
  fee: {
    cleaning: 'Cleaning fee',
    tourist_tax: 'Tourist tax',
    linen: 'Linen',
    pet: 'Pet',
    parking: 'Parking',
    extra_person: 'Extra person',
    extra_person_child: 'Extra person (child)',
    child_discount: 'Children’s discount',
    accommodation: 'Accommodation',
    discount: 'Discount',
  },
  quote: {
    title: 'Quote',
    valid_until: 'Valid until',
    send: 'Send quote',
    accepted: 'Accepted',
    declined: 'Declined',
  },
  mail: {
    ownProgram: 'You send from your own mail program: print the document here or '
      + 'save it as a PDF, then send it to the guest yourself.',
  },
  document: {
    print: 'Print',
    savePdf: 'Save PDF',
    sharePdf: 'Share PDF',
    preparing: 'Preparing the PDF …',
    draftBlocked: 'This invoice is still a draft and carries no number. Issue it '
      + 'first — the PDF is created after that.',
    charsetBlocked: 'The character “{z}” cannot be set in the PDF. Names are never '
      + 'transliterated — please report it and the font will be extended.',
    saveFailed: 'The file could not be handed over. Please try again or print the '
      + 'document instead.',
    pdfFailed: 'The PDF could not be created. You can still print the document.',
  },
  invoice: {
    title: 'Invoice',
    number: 'Invoice number',
    issued: 'Invoice date',
    due: 'Due',
    cancel: 'Cancel invoice',
    cancelReason: 'Reason for the cancellation (printed on the credit note):',
    cancelled: 'Cancelled',
    creditNote: 'Credit note',
    payLink: 'Payment link',
    issue: 'Issue invoice',
    draftHint: 'Draft — no invoice number yet and still editable. Issuing assigns the number and locks the invoice.',
    draft: 'Draft',
    archive: 'Invoice archive',
    drafts: 'Drafts',
    overdue: 'Overdue',
    markPaid: 'Mark as paid',
    collect: 'Take payment',
    paidOn: 'Payment received on',
    smallBusiness: 'Small business scheme — no VAT charged.',
  },
  rksv: {
    // Vorgang 018: order flow of the RKSV add-on. No amount in these texts
    // (AK12) — {preis} is filled from modul_preise at runtime.
    modul: {
      titel: 'Fiscalisation add-on',
      beschreibung: 'Signed receipts through a cloud register (fiskaltrust), provisioned by AUVISO — as a bookable add-on module.',
      preisMonat: '{preis} per month plus VAT.',
      bestellt: 'Ordered — being set up.',
      zusage: 'Your register is usually active within two working days.',
      termin: 'Real activations only become possible once the fiskaltrust framework contract is signed — expected mid-October. Your order is on file; we will let you know as soon as it starts.',
      aktivAb: 'Active since {datum}',
      zuschlagAb: 'Surcharge from {datum}',
      mindestbezugBis: 'Minimum term until {datum}',
      gekuendigtZum: 'Cancelled effective {datum}',
      storniert: 'Order withdrawn.',
    },
    bestellung: {
      einstieg: 'Order fiscalisation',
      nurOwner: 'You can see the module status here; only the account owner can order or cancel.',
      nurWeb: 'The add-on can currently only be booked on accounts with web billing.',
      nummerFehlt: 'Please store your VAT ID or tax number in the master data first. A wrong number only surfaces at the FinanzOnline registration and requires a certificate swap via fiskaltrust support — that effort is billed.',
      nummer: 'Number on file: {nummer}',
      bestaetigenNummer: 'This number is correct.',
      bestaetigenKosten: 'I have read the cost summary and place a binding order for the add-on.',
      absenden: 'Order with obligation to pay',
      stornieren: 'Withdraw the order',
      stornierenFrage: 'Withdraw the order? This is free of charge — nothing has been set up yet.',
      kuendigen: 'Cancel the module',
      kuendigenFrage: 'Cancel the module effective at the end of the month? The current month is still billed in full; the register keeps signing until then, and deregistering it in FinanzOnline is your duty afterwards.',
      kuendigenAb: 'Cancellable at the earliest effective {datum}.',
      veraltet: 'The cost summary is out of date — please reload and confirm again.',
    },
    kosten: {
      titel: 'Cost summary',
      preis: 'Module price: {preis} per month plus VAT, in addition to your plan price.',
      pflichten: 'Only you yourself can do these: register the till and its signature unit in FinanzOnline, verify the opening receipt with the BMF Belegcheck app, and have the annual receipt checked each year by 15 February.',
      support: 'Effort caused by incorrectly stored data — in particular a wrong tax number / VAT ID (certificate swap via fiskaltrust support) or a typo in the AES key in FinanzOnline — is billed by actual effort at the hourly rate in force.',
      supportWeg: 'fiskaltrust provides no end-customer support; your contact is AUVISO.',
      meldepflicht: 'Register outages of more than 48 hours must be reported by you in FinanzOnline.',
      mindestbezug: 'Minimum term of twelve months, thereafter cancellable monthly to the end of a month.',
      beginn: 'The surcharge starts with the month after activation; the rest of the activation month is free of charge.',
    },
    pflichten: {
      titel: 'Your duties as the operator of the register',
      finanzonline: 'Only you yourself can register the till and its signature unit in FinanzOnline.',
      startbeleg: 'Verify the opening receipt with the BMF Belegcheck app — only that check confirms the chain.',
      jahresbeleg: 'Have the annual receipt checked in FinanzOnline each year by 15 February.',
      dep: 'The audit log (DEP) must be kept for seven years. StaySuite files a weekly export — that supports you, but does not replace your duty as the business owner.',
      kuendigung: 'Minimum term of twelve months; thereafter cancellable monthly to the end of a month, and the cancellation month is billed as the last full month.',
      support: 'Effort caused by an incorrectly stored tax number or VAT ID is billed by actual effort at the hourly rate in force.',
      abmeldung: 'Deregistering the till in FinanzOnline is your duty — legally the register belongs to you, not to StaySuite.',
    },
  },
  steuernummer: {
    label: 'Tax number',
    hint: 'Austrian tax number (nine digits), e.g. 12-345/6789. Leave empty if you only have a VAT ID.',
    invalid: 'Not a valid tax number — expected are nine digits, e.g. 12-345/6789. Nothing was saved.',
  },
  touristTax: {
    title: 'Tourist tax',
    municipality: 'Municipality',
    perPersonNight: 'per person per night',
    exemptUnder: 'Exempt under age {n}',
    report: 'Tourist tax return',
    personNights: 'Person-nights',
    liable: 'Liable',
    exempt: 'Exempt',
    unknownAges: "Children's ages unknown — counted as liable",
  },
  housekeeping: {
    title: 'Housekeeping',
    turnover: 'Turnover',
    turnoverHint: 'Departure AND arrival today — must be ready before check-in.',
    nextArrival: 'Next arrival',
    noArrival: 'No follow-on booking',
    done: 'Done',
    nothing: 'Nothing to clean today.',
    departed: 'Departed',
  },
  dashboard: {
    todayArrivals: 'Arrivals today',
    todayDepartures: 'Departures today',
    openInvoices: 'Open invoices',
    occupancy30: 'Occupancy next 30 days',
    revenue: 'Revenue',
  },
  abo: {
    laden: {
      titel: 'Subscription',
      kaufen: 'Subscribe',
      wiederherstellen: 'Restore purchases',
      verwalten: 'Manage subscription',
      verwaltenHinweis: 'Cancelling and switching plans happens in the App Store. “Manage subscription” opens the subscription settings of your account.',
      nurInhaber: 'The subscription is set up by the account owner.',
      zustandAktiv: 'Your subscription is active.',
      zustandTest: 'Trial runs until {datum}.',
      zustandLesend: 'This account is currently read-only. Your data is kept.',
      andererKanal: 'This business already pays through a different channel.',
      fremderBetrieb: 'This subscription already belongs to a different business.',
      erstattungBeimLaden: 'The payment could not be matched. Refunds are handled by the App Store, not by AUVISO.',
      pruefbuild: 'Internal test build — the purchase environment is determined by the App Store.',
      sandkasten: 'Sandbox environment — nothing will be charged.',
      ladeFehler: 'Products could not be loaded from the store.',
      abgebrochen: 'The purchase was cancelled.',
      ausstehend: 'The payment is awaiting confirmation by the App Store.',
      aktiviert: 'Your subscription was confirmed and is active.',
      keinKauf: 'No purchases were found for this Apple account.',
      kaufFehler: 'The purchase was not completed.',
      gemeldet: 'The purchase was reported to the server.',
      kaufVorhanden: 'Your subscription runs through the App Store.',
    },
  },
}
